const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getAuthToken = () => localStorage.getItem('access_token');

// Dispatch a global event when the session expires so AppContext can react
const dispatchSessionExpired = () => {
  window.dispatchEvent(new CustomEvent('session-expired'));
};

const authFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    // Token expired or invalid -- clear storage and redirect to login
    localStorage.removeItem('access_token');
    localStorage.removeItem('current_user');
    dispatchSessionExpired();
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
};

export const api = {
  // Auth
  register: async (userData) => {
    return authFetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials) => {
    const response = await authFetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    localStorage.setItem('access_token', response.access_token);
    return response;
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('current_user');
  },

  // Admin endpoints
  adminCreateUser: async (userData) => {
    return authFetch(`${API_BASE_URL}/api/admin/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  adminListUsers: async () => {
    return authFetch(`${API_BASE_URL}/api/admin/users`);
  },

  adminDeleteUser: async (userId) => {
    return authFetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  },

  adminUpdateUserRole: async (userId, role) => {
    return authFetch(`${API_BASE_URL}/api/admin/users/${userId}/role?role=${role}`, {
      method: 'PATCH',
    });
  },

  adminChangePassword: async (userId, newPassword) => {
    return authFetch(`${API_BASE_URL}/api/admin/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  // Workshops
  getWorkshops: async () => {
    return authFetch(`${API_BASE_URL}/api/workshops`);
  },

  getWorkshop: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}`);
  },

  createWorkshop: async (workshopData) => {
    return authFetch(`${API_BASE_URL}/api/workshops`, {
      method: 'POST',
      body: JSON.stringify(workshopData),
    });
  },

  updateWorkshop: async (workshopId, workshopData) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}`, {
      method: 'PATCH',
      body: JSON.stringify(workshopData),
    });
  },

  deleteWorkshop: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}`, {
      method: 'DELETE',
    });
  },

  getArchivedWorkshops: async () => {
    return authFetch(`${API_BASE_URL}/api/workshops/archived`);
  },

  restoreWorkshop: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/restore`, {
      method: 'POST',
    });
  },

  purgeWorkshop: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/purge`, {
      method: 'DELETE',
    });
  },

  verifyWorkshopPassword: async (workshopId, password) => {
    return authFetch(`${API_BASE_URL}/api/workshops/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ workshop_id: workshopId, password }),
    });
  },

  getWorkshopParticipants: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/participants`);
  },

  getAvailableWorkshopUsers: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/available-users`);
  },

  addWorkshopParticipant: async (workshopId, userId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role: 'member' }),
    });
  },

  removeWorkshopParticipant: async (workshopId, userId) => {
    return authFetch(`${API_BASE_URL}/api/workshops/${workshopId}/participants/${userId}`, {
      method: 'DELETE',
    });
  },

  // Sessions
  getSessions: async (workshopId = null) => {
    const query = workshopId ? `?workshop_id=${workshopId}` : '';
    return authFetch(`${API_BASE_URL}/api/sessions${query}`);
  },

  getSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/sessions/${sessionId}`);
  },

  createSession: async (sessionData) => {
    return authFetch(`${API_BASE_URL}/api/sessions`, {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  },

  moveSession: async (sessionId, workshopId) => {
    return authFetch(`${API_BASE_URL}/api/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workshop_id: workshopId }),
    });
  },

  deleteSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  getArchivedSessions: async (workshopId = null) => {
    const query = workshopId ? `?workshop_id=${workshopId}` : '';
    return authFetch(`${API_BASE_URL}/api/sessions/archived${query}`);
  },

  restoreSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/sessions/${sessionId}/restore`, {
      method: 'POST',
    });
  },

  purgeSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/sessions/${sessionId}/purge`, {
      method: 'DELETE',
    });
  },

  deleteWritingSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  getArchivedWritingSessions: async (workshopId = null) => {
    const query = workshopId ? `?workshop_id=${workshopId}` : '';
    return authFetch(`${API_BASE_URL}/api/writing/sessions/archived${query}`);
  },

  restoreWritingSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}/restore`, {
      method: 'POST',
    });
  },

  purgeWritingSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}/purge`, {
      method: 'DELETE',
    });
  },

  deleteContribution: async (contributionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/contributions/${contributionId}`, {
      method: 'DELETE',
    });
  },

  // Uncertainties
  getUncertainties: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/uncertainties?session_id=${sessionId}`);
  },

  createUncertainty: async (uncertaintyData) => {
    return authFetch(`${API_BASE_URL}/api/uncertainties`, {
      method: 'POST',
      body: JSON.stringify(uncertaintyData),
    });
  },

  deleteUncertainty: async (uncertaintyId) => {
    return authFetch(`${API_BASE_URL}/api/uncertainties/${uncertaintyId}`, {
      method: 'DELETE',
    });
  },

  // Votes
  getVotes: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/votes?session_id=${sessionId}`);
  },

  submitVote: async (voteData) => {
    return authFetch(`${API_BASE_URL}/api/votes`, {
      method: 'POST',
      body: JSON.stringify(voteData),
    });
  },

  // AI endpoints
  chatWithAI: async (message, sessionId = null) => {
    return authFetch(`${API_BASE_URL}/api/ai/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, session_id: sessionId }),
    });
  },

  suggestUncertainties: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/ai/suggest-uncertainties`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  },

  analyzeVotes: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/ai/analyze-votes?session_id=${sessionId}`, {
      method: 'POST',
    });
  },

  getChatHistory: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/ai/chat/history/${sessionId}`);
  },

  getSectionChatHistory: async (sectionId) => {
    return authFetch(`${API_BASE_URL}/api/ai/chat/section/${sectionId}`);
  },

  // Writing Sessions
  getWritingSessions: async (workshopId = null) => {
    const query = workshopId ? `?workshop_id=${workshopId}` : '';
    return authFetch(`${API_BASE_URL}/api/writing/sessions${query}`);
  },

  getWritingSession: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}`);
  },

  createWritingSession: async (sessionData) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions`, {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  },

  createWritingSection: async (sectionData) => {
    return authFetch(`${API_BASE_URL}/api/writing/sections`, {
      method: 'POST',
      body: JSON.stringify(sectionData),
    });
  },

  updateWritingSection: async (sectionId, sectionData) => {
    return authFetch(`${API_BASE_URL}/api/writing/sections/${sectionId}`, {
      method: 'PATCH',
      body: JSON.stringify(sectionData),
    });
  },

  addContribution: async (contributionData) => {
    return authFetch(`${API_BASE_URL}/api/writing/contributions`, {
      method: 'POST',
      body: JSON.stringify(contributionData),
    });
  },

  getSectionContributions: async (sectionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sections/${sectionId}/contributions`);
  },

  customAIPrompt: async (sectionId, userInput) => {
    return authFetch(`${API_BASE_URL}/api/ai/custom-prompt`, {
      method: 'POST',
      body: JSON.stringify({ section_id: sectionId, user_input: userInput }),
    });
  },

  updateWritingSession: async (sessionId, sessionData) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(sessionData),
    });
  },

  moveWritingSession: async (sessionId, workshopId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ workshop_id: workshopId }),
    });
  },

  verifyWritingSessionPassword: async (sessionId, password) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, password }),
    });
  },

  getSessionVenn: async (sessionId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}/venn`);
  },

  saveSessionVenn: async (sessionId, vennState) => {
    return authFetch(`${API_BASE_URL}/api/writing/sessions/${sessionId}/venn`, {
      method: 'PUT',
      body: JSON.stringify(vennState),
    });
  },

  compareVenn: async (sectionAId, sectionBId) => {
    return authFetch(`${API_BASE_URL}/api/ai/compare-venn`, {
      method: 'POST',
      body: JSON.stringify({ section_a_id: sectionAId, section_b_id: sectionBId }),
    });
  },

  getSectionsByWorkshop: async (workshopId) => {
    return authFetch(`${API_BASE_URL}/api/writing/sections/by-workshop?workshop_id=${workshopId}`);
  },

  crossSectionAnalysis: async (sectionIds, prompt) => {
    return authFetch(`${API_BASE_URL}/api/ai/cross-section-analysis`, {
      method: 'POST',
      body: JSON.stringify({ section_ids: sectionIds, prompt }),
    });
  },

  extractTakeaways: async (sectionId, transcript) => {
    return authFetch(`${API_BASE_URL}/api/ai/extract-takeaways`, {
      method: 'POST',
      body: JSON.stringify({ section_id: sectionId, transcript }),
    });
  },

  verifySectionPassword: async (sectionId, password) => {
    return authFetch(`${API_BASE_URL}/api/ai/verify-section-password`, {
      method: 'POST',
      body: JSON.stringify({ section_id: sectionId, password }),
    });
  },
};
